import type { AuthUser } from "@dentacore/shared-types";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import * as client from "../api/client";
import { REFRESH_TOKEN_KEY } from "../api/client";
import { activeBranch } from "../api/tokens";
import { SessionSwitched } from "./session-switched";
import { apexOrigin } from "./apex-origin";
import { ACCESS_CHANGED } from "./access-events";

/**
 * `switched` is a fourth real state, like `restoring` is a third.
 *
 * It means the stored session is valid but belongs to somebody other than the
 * person this tab was acting as — see `SESSION_OWNER_KEY` in `tokens.ts`. The
 * application is deliberately not rendered while it holds, so no screen is ever
 * drawn for one user under another's assumption.
 */
type Status = "restoring" | "signed-in" | "signed-out" | "switched";

interface AuthState {
  status: Status;
  user: AuthUser | null;
  /**
   * Returns the signed-in user rather than void, so the caller can route on
   * their role without a second round trip — the login page sends a new
   * clinic's admin to `/setup` and nobody else (STORY-069).
   */
  /**
   * `practice` is only supplied when the address bar names no clinic and the
   * server has said it needs one; a subdomain always takes precedence over it.
   */
  signIn: (email: string, password: string, practice?: string) => Promise<AuthUser>;
  /**
   * Spend an invite link. Ends signed in, exactly as `signIn` does — the
   * invite was the credential, so there is no second step.
   */
  acceptInvite: (token: string, password: string | undefined) => Promise<void>;
  /**
   * Spend a sign-up hand-off. Ends signed in, and there is no password step —
   * one was chosen at sign-up, seconds earlier and at another origin.
   */
  signInWithHandoff: (token: string) => Promise<void>;
  /**
   * Spend a chooser hand-off, at the practice the person picked.
   *
   * Returns the user, unlike its sign-up sibling, so the arrival can route on
   * `canAdminister` exactly as `signIn` does. A chooser lands a returning
   * person in a practice that may still be half set up, and sending them to
   * Home when sign-in would have sent them to `/setup` would make the door
   * they came through change where they end up.
   */
  signInWithChoice: (token: string) => Promise<AuthUser>;
  signOut: () => Promise<void>;
  /**
   * Accept a session that belongs to somebody else — only reachable from the
   * `switched` screen, where whose it is has just been said out loud.
   */
  adoptSwitchedSession: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Holds the session for the whole application.
 *
 * `restoring` is a real third state, not a loading flag. On a cold load the app
 * does not yet know whether the stored refresh token is still good, and the
 * router must not decide between the login screen and the app until it does —
 * otherwise every reload flashes the login screen before redirecting away.
 */
export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [status, setStatus] = useState<Status>("restoring");
  const [user, setUser] = useState<AuthUser | null>(null);
  /**
   * Who the tab has become, when that is known.
   *
   * Known on a cold load — the restore already asked the server. Null when the
   * switch was spotted live by the `storage` listener, which deliberately does
   * not spend the new token to find out. Null therefore means "somebody else",
   * not "nobody".
   */
  const [switchedTo, setSwitchedTo] = useState<AuthUser | null>(null);

  const applyRestore = useCallback((restored: client.RestoredSession) => {
    if (restored.kind === "none") {
      setUser(null);
      setSwitchedTo(null);
      setStatus("signed-out");
      return;
    }
    setUser(restored.user);
    setSwitchedTo(restored.kind === "switched" ? restored.user : null);
    setStatus(restored.kind === "switched" ? "switched" : "signed-in");
  }, []);

  useEffect(() => {
    let cancelled = false;

    /**
     * **Arrived here by signing out somewhere else, so do not resume anything.**
     *
     * Read before the restore rather than after, because restoring first would
     * mint a session — `restoreSession` spends the refresh token — and then
     * throw it away, which is a wasted rotation and a window in which the
     * application is briefly signed in as somebody the reader did not choose.
     *
     * The marker is stripped from the address bar afterwards so that a reload,
     * or a link copied out of it, does not keep signing people out of a page
     * they have since signed into.
     */
    if (new URLSearchParams(window.location.search).has("signed-out")) {
      client.endSessionLocally();
      const clean = new URL(window.location.href);
      clean.searchParams.delete("signed-out");
      window.history.replaceState(null, "", clean.toString());
    }

    void client.restoreSession().then((restored) => {
      if (cancelled) return;
      applyRestore(restored);
    });

    return () => {
      cancelled = true;
    };
  }, [applyRestore]);

  /**
   * **What somebody may do depends on where they are working** (R-D6), so the
   * permissions are read again once a location is known and every time it
   * changes. Sign-in hands over everything held at any location, because no
   * location has been picked yet; the branch switcher picks one a moment later
   * and this replaces that with the location's own (QA, 2026-10-04).
   */
  useEffect(() => {
    if (status !== "signed-in") return undefined;
    let alive = true;
    const refresh = (): void => {
      if (activeBranch.get() === null) return;
      void client
        .currentUser()
        .then((fresh) => {
          if (alive) setUser(fresh);
        })
        .catch(() => undefined);
    };
    refresh();
    const unsubscribe = activeBranch.subscribe(refresh);
    // **And whenever they may have changed** — the tab coming back, or a
    // request refused for a permission — so a role given or taken away reaches
    // an open tab without a reload, as the modules already do. A refusal is
    // read again at most every two seconds, so a screen retrying cannot loop.
    let lastRefused = 0;
    const onVisible = (): void => {
      if (document.visibilityState === "visible") refresh();
    };
    const onRefused = (): void => {
      if (Date.now() - lastRefused < 2_000) return;
      lastRefused = Date.now();
      refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", refresh);
    window.addEventListener(ACCESS_CHANGED, onRefused);
    return () => {
      alive = false;
      unsubscribe();
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refresh);
      window.removeEventListener(ACCESS_CHANGED, onRefused);
    };
  }, [status]);

  /**
   * Notice a sign-in that happened in another tab, while this one is open.
   *
   * `storage` fires only in the *other* tabs, never the one that wrote — so the
   * tab doing the signing in never warns itself. Without this the switch is
   * invisible until something forces a reload, which is precisely how it was
   * reported: a tab that had been fine for minutes came back as a colleague.
   *
   * ── This handler must not touch the network, and that is not a preference ──
   *
   * The obvious version asks *who* the new token belongs to by restoring with
   * it. Refresh tokens rotate and are single-use, and `refreshAccessToken`'s
   * own comment says what a second use looks like from the server: **a replay,
   * which is treated as a leaked token, so every session the user holds is
   * revoked.** `coordinatedRefresh` de-duplicates within one tab only — it is a
   * module-level promise, and tabs do not share modules.
   *
   * So the first cut of this fix signed *both* tabs out: this listener spent
   * the token the other tab had just minted for itself. Knowing *that* the
   * session changed is enough to stop and say so; knowing *who* it now is can
   * wait for the reload that adopting it performs anyway.
   */
  useEffect(() => {
    function onStorage(event: StorageEvent): void {
      if (event.key !== null && event.key !== REFRESH_TOKEN_KEY) return;

      // A sign-out elsewhere clears the slot. Nothing to adopt, and nothing to
      // warn about beyond ending this session too.
      if (event.newValue === null) {
        setUser(null);
        setSwitchedTo(null);
        setStatus("signed-out");
        return;
      }

      // Same token written again — a rotation this tab's own traffic caused, or
      // a duplicate event. Nothing changed hands.
      if (event.newValue === event.oldValue) return;

      setStatus((was) => (was === "signed-in" ? "switched" : was));
    }

    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  /**
   * Accept the session this tab found. The person has been told whose it is,
   * which is the whole of what `switched` was protecting.
   */
  const adoptSwitchedSession = useCallback(() => {
    if (switchedTo === null) {
      // Spotted live, so this tab has never asked who the new session belongs
      // to. A reload takes the ordinary cold-start path, which spends the
      // token exactly once — the only safe way to find out. Clearing the
      // binding first stops that reload reporting the same switch again.
      client.releaseSessionForThisTab();
      window.location.reload();
      return;
    }
    // Found on a cold load: already restored, already known. Nothing to fetch.
    client.claimSessionForThisTab(switchedTo.id);
    setSwitchedTo(null);
    setStatus("signed-in");
  }, [switchedTo]);

  const signIn = useCallback(async (email: string, password: string, practice?: string) => {
    const signedIn = await client.login(email, password, practice);
    setUser(signedIn);
    setStatus("signed-in");
    return signedIn;
  }, []);

  const acceptInvite = useCallback(async (token: string, password: string | undefined) => {
    const signedIn = await client.acceptInvite(token, password);
    setUser(signedIn);
    setStatus("signed-in");
  }, []);

  const signInWithHandoff = useCallback(async (token: string) => {
    const signedIn = await client.spendHandoff(token);
    setUser(signedIn);
    setStatus("signed-in");
  }, []);

  const signInWithChoice = useCallback(async (token: string) => {
    const signedIn = await client.spendChoice(token);
    setUser(signedIn);
    setStatus("signed-in");
    return signedIn;
  }, []);

  /**
   * End the session — **and, for somebody who works at more than one practice,
   * leave them somewhere they can reach the others** (2026-09-20).
   *
   * ── Why this leaves the origin at all ───────────────────────────────────
   *
   * The chooser only exists at the apex. `resolveTenant` reads the clinic from
   * the subdomain, so a sign-in *at* a practice is scoped to that practice by
   * construction and can never offer a choice — which meant that signing out
   * at `their-practice.dentacore.app` left you on a login that could only ever
   * let you back into the practice you had just left. The client's words:
   * *"i cannot move from branch to branch although i have access to both"*.
   * It was never a browser or a cookie problem; there was simply no door.
   *
   * ── Why the apex rather than a "switch practice" link ───────────────────
   *
   * Because one of them is a second way to do the same thing and the other is
   * the only way. A link would have to live on a screen that is already the
   * wrong screen, and somebody who signed out for the ordinary reason — they
   * are done — would still be looking at it. Sending them to the one place
   * that can ask the question is fewer moving parts and one less thing to
   * forget to draw. Chosen by the client on 2026-09-20.
   *
   * ── And why the session is revoked *before* the navigation ──────────────
   *
   * `client.logout()` is awaited, so the refresh token is dead on the server
   * and cleared from this origin's `localStorage` before anything moves. A
   * redirect fired first would race the request it depends on, and a failed
   * revoke would leave a live token behind on a shared machine.
   */
  const signOut = useCallback(async () => {
    await client.logout();
    setUser(null);
    setStatus("signed-out");

    const apex = apexOrigin();
    // Already at the apex — single-tenant installs, and anyone who signed in
    // there. Nothing to travel to, and a redirect would only cost a reload.
    if (apex === null) return;

    /**
     * **`?signed-out` is not decoration — it is the whole of the fix** for the
     * defect this redirect introduced.
     *
     * `localStorage` is per-origin, so clearing the practice's session says
     * nothing about the apex's. A browser that had signed in at the apex — an
     * ordinary thing for somebody whose practice is their only one — arrived
     * here and had that session **restored**, so pressing Sign out at one
     * clinic landed the reader inside somebody else's account, refresh token
     * intact. On a shared machine that is the wrong outcome for a control with
     * this label.
     *
     * The clearing cannot happen before the redirect, because the origin that
     * holds the credential is the one being navigated to. So the intent is
     * carried across in the URL and acted on by the arriving page.
     *
     * `replace`, not `assign`: the practice just left must not be one Back
     * away from a sign-out.
     */
    window.location.replace(`${apex}/login?signed-out=1`);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      signIn,
      acceptInvite,
      signInWithHandoff,
      signInWithChoice,
      signOut,
      adoptSwitchedSession,
    }),
    [
      status,
      user,
      signIn,
      acceptInvite,
      signInWithHandoff,
      signInWithChoice,
      signOut,
      adoptSwitchedSession,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {/*
        `switched` replaces the application rather than covering it. The token
        in hand belongs to `user` — the new holder — so rendering the routes
        behind this would be drawing their data for whoever was here before.
      */}
      {status === "switched" ? (
        <SessionSwitched
          nowSignedIn={switchedTo}
          onContinue={adoptSwitchedSession}
          onSignOut={() => void signOut()}
        />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (context === null) throw new Error("useAuth must be used inside <AuthProvider>");
  return context;
}
